import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import { Label, LABEL_TYPE, SelectableCard, Spinner } from '@/components/generic';
import { TEXT_OVERLAY } from '@/constants';
import { getThumbnailByType } from '@/helpers';
import { PhotoFilmIcon } from '@/resources';
import { Asset } from '@/types';

type AssetGridPickerProps = {
  assets: Asset[];
  selectedAssetId: number | null;
  onAssetSelect: (asset: Asset) => void;
  onAssetEdit?: (asset: Asset) => void;
  isLoading: boolean;
  emptyLabel: string;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  isCardEditable?: boolean;
};

export const AssetGridPicker = ({
  assets,
  selectedAssetId,
  onAssetSelect,
  onAssetEdit,
  isLoading,
  emptyLabel,
  hasNextPage,
  fetchNextPage,
  isCardEditable
}: AssetGridPickerProps) => {
  const { ref: scrollTriggerRef, inView } = useInView();

  useEffect(() => {
    if (inView && hasNextPage && fetchNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage]);

  if (isLoading) {
    return (
      <div className='flex h-full justify-center py-8'>
        <Spinner />
      </div>
    );
  }

  if (assets.length === 0) {
    return (
      <div className='flex h-full flex-col items-center justify-center py-8'>
        <PhotoFilmIcon className='mb-2 size-8 text-secondary-gray' />
        <Label text={emptyLabel} type={LABEL_TYPE.PARAGRAPH2} className='text-secondary-gray' />
      </div>
    );
  }

  return (
    <>
      <div className='grid grid-cols-4 gap-4'>
        {assets.map((asset: Asset) => (
          <SelectableCard
            key={asset.id}
            imageUrl={getThumbnailByType(asset)}
            isSelected={selectedAssetId === asset.id}
            label={asset.assetName}
            onSelect={() => onAssetSelect(asset)}
            isEditable={isCardEditable ? asset.assetType === TEXT_OVERLAY : false}
            onEdit={
              isCardEditable && asset.assetType === TEXT_OVERLAY
                ? () => onAssetEdit?.(asset)
                : undefined
            }
          />
        ))}
      </div>

      <div ref={scrollTriggerRef} className='h-1 w-full' />
    </>
  );
};
